import { useCallback, useEffect, useState } from 'react';
import type {
  IGitHubAdminStatus,
  IGitHubCollaboratorsSyncResult,
  IGitHubMemberSyncResult,
  IPersonAutoMatchResult,
} from '@skystone/shared';
import { personApi } from '@/services/person-api';
import { getErrorMessage } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { ErrorBanner, Spinner } from '@/components/ui';
import { GitHubSyncStatusCard } from './GitHubSyncStatusCard';

const ORG_MEMBER_TITLE = 'GitHub org members';
const ORG_MEMBER_DESCRIPTION =
  'Caches every member of the GitHub organization with their profile data. Feeds the auto-match.';
const REPO_COLLABORATOR_TITLE = 'GitHub repo collaborators';
const REPO_COLLABORATOR_DESCRIPTION =
  'Caches per-repo access (admin/maintain/write/triage/read) across every non-archived repo.';

function MatchBreakdown({ match }: Readonly<{ match: IPersonAutoMatchResult | null }>) {
  if (match === null) return null;
  return (
    <div className="rounded-md border bg-muted/40 p-3 text-sm">
      <p className="font-medium">Auto-match post-step</p>
      <ul className="mt-1 space-y-0.5 text-muted-foreground">
        <li>Linked: {match.linked}</li>
        <li>Skipped (no email): {match.skippedNoEmail}</li>
        <li>Skipped (bot/org): {match.skippedBot}</li>
        <li>Skipped (already linked): {match.skippedAlreadyLinked}</li>
        <li>Ambiguous: {match.ambiguous}</li>
      </ul>
    </div>
  );
}

function MemberSyncResultBanner({
  result,
}: Readonly<{ result: IGitHubMemberSyncResult | null }>) {
  if (result === null) return null;
  return (
    <div className="space-y-2">
      <div className="rounded-md border bg-muted/40 p-3 text-sm">
        <p className="font-medium">Last run summary</p>
        <ul className="mt-1 space-y-0.5 text-muted-foreground">
          <li>Members synced: {result.sync.total}</li>
        </ul>
      </div>
      <MatchBreakdown match={result.match} />
    </div>
  );
}

function CollaboratorSyncResultBanner({
  result,
}: Readonly<{ result: IGitHubCollaboratorsSyncResult | null }>) {
  if (result === null) return null;
  const syncedRepos =
    result.sync.totalRepos - result.sync.skippedNoAccess.length - result.sync.failedRepos.length;
  return (
    <div className="space-y-2">
      <div className="rounded-md border bg-muted/40 p-3 text-sm">
        <p className="font-medium">Last run summary</p>
        <ul className="mt-1 space-y-0.5 text-muted-foreground">
          <li>Repos synced: {syncedRepos}</li>
          <li>Collaborators cached: {result.sync.totalCollaborators}</li>
          {result.sync.skippedNoAccess.length > 0 && (
            <li title={result.sync.skippedNoAccess.join(', ')}>
              Skipped (no access): {result.sync.skippedNoAccess.length}
            </li>
          )}
          {result.sync.failedRepos.length > 0 && (
            <li>Failed repos: {result.sync.failedRepos.join(', ')}</li>
          )}
        </ul>
      </div>
      <MatchBreakdown match={result.match} />
    </div>
  );
}

export function GitHubAdminTab() {
  const [status, setStatus] = useState<IGitHubAdminStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [memberRunning, setMemberRunning] = useState(false);
  const [collaboratorRunning, setCollaboratorRunning] = useState(false);
  const [memberResult, setMemberResult] = useState<IGitHubMemberSyncResult | null>(null);
  const [collaboratorResult, setCollaboratorResult] =
    useState<IGitHubCollaboratorsSyncResult | null>(null);

  const loadStatus = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const next = await personApi.github.getStatus();
      setStatus(next);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load GitHub status'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleRunMember = async (): Promise<void> => {
    setMemberRunning(true);
    setError(null);
    try {
      const result = await personApi.github.triggerMemberSync();
      setMemberResult(result);
      await loadStatus();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to run GitHub org-member sync'));
    } finally {
      setMemberRunning(false);
    }
  };

  const handleRunCollaborator = async (): Promise<void> => {
    setCollaboratorRunning(true);
    setError(null);
    try {
      const result = await personApi.github.triggerCollaboratorSync();
      setCollaboratorResult(result);
      await loadStatus();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to run GitHub repo-collaborator sync'));
    } finally {
      setCollaboratorRunning(false);
    }
  };

  if (loading && status === null) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  const tokenConfigured = status?.tokenConfigured ?? false;
  const triggersDisabled = !tokenConfigured;

  return (
    <div className="space-y-4">
      <ErrorBanner message={error} />

      {!tokenConfigured && (
        <div
          role="alert"
          className="rounded-md border border-warning-200 bg-warning-50 p-3 text-sm text-warning-700"
        >
          <p className="font-medium">GITHUB_TOKEN is not configured</p>
          <p className="mt-1">
            Set the <code>GITHUB_TOKEN</code> env var and restart the worker to enable GitHub
            syncs. Manual triggers are disabled until it is present.
          </p>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <GitHubSyncStatusCard
          title={ORG_MEMBER_TITLE}
          description={ORG_MEMBER_DESCRIPTION}
          status={status?.orgMemberSync ?? null}
          isRunning={memberRunning}
          disabled={triggersDisabled}
          onRun={handleRunMember}
          resultContent={<MemberSyncResultBanner result={memberResult} />}
        />
        <GitHubSyncStatusCard
          title={REPO_COLLABORATOR_TITLE}
          description={REPO_COLLABORATOR_DESCRIPTION}
          status={status?.repoCollaboratorSync ?? null}
          isRunning={collaboratorRunning}
          disabled={triggersDisabled}
          onRun={handleRunCollaborator}
          resultContent={<CollaboratorSyncResultBanner result={collaboratorResult} />}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cached counts</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-sm">
            <dt className="text-muted-foreground">Org members cached</dt>
            <dd className="font-medium">{status?.memberCount ?? 0}</dd>
            <dt className="text-muted-foreground">Repo collaborators cached</dt>
            <dd className="font-medium">{status?.collaboratorCount ?? 0}</dd>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}
